import moment, { Moment, MomentInput } from 'moment-timezone';
import { IJob } from '@medely/types';

export const setDateToTimeMoment = (
  date: MomentInput,
  time: MomentInput,
  ignoreDateTz = true,
): Moment => {
  const dateMoment = ignoreDateTz ? moment.utc(date) : moment(date);
  const timeMoment = moment(time);

  const dateTimeMoment = timeMoment.clone();
  const dateTz = dateMoment.tz();
  if (dateTz) {
    dateTimeMoment.tz(dateTz);
  }
  dateTimeMoment.set('year', dateMoment.year());
  dateTimeMoment.set('month', dateMoment.month());
  dateTimeMoment.set('date', dateMoment.date());

  if (dateTimeMoment.isValid()) {
    return dateTimeMoment;
  } else {
    throw new Error('Invalid datetime');
  }
};

export const deriveDate = (
  job: IJob,
  time: Moment,
  mode: 'clock_in' | 'clock_out',
): string | null => {
  const timezone = job.location.timezone_lookup as string;
  const startMoment = moment(job.current_starts_time).tz(timezone);
  const endMoment = moment(job.current_ends_time).tz(timezone);
  const currentMoment = moment(time).tz(timezone);

  if (startMoment.isAfter(endMoment)) {
    throw new Error('current_starts_time is after current_ends_time');
  } else if (endMoment.diff(startMoment, 'days') > 1) {
    throw new Error('current_starts_time and current_ends_time are more than one day apart');
  }

  const timeAtStartDate = setDateToTimeMoment(startMoment, time, false);
  const timeAtEndDate = setDateToTimeMoment(endMoment, time, false);

  if (mode === 'clock_out') {
    if (timeAtStartDate.isBefore(startMoment, 'minute')) {
      return startMoment.clone().add(1, 'day').format('YYYY-MM-DD');
    }
  } else {
    if (timeAtEndDate.isAfter(endMoment, 'minute')) {
      if (!job.location.professional_can_adjust_times && currentMoment.isAfter(endMoment)) {
        return currentMoment.format('YYYY-MM-DD');
      }
      return endMoment.clone().subtract(1, 'day').format('YYYY-MM-DD');
    }
  }

  if (timeAtStartDate.isBetween(startMoment, endMoment)) {
    return timeAtStartDate.format('YYYY-MM-DD');
  }
  if (timeAtEndDate.isBetween(startMoment, endMoment)) {
    return timeAtEndDate.format('YYYY-MM-DD');
  }

  return mode === 'clock_in' ? startMoment.format('YYYY-MM-DD') : endMoment.format('YYYY-MM-DD');
};

export type JobForIsOvernight = Pick<
  IJob,
  'current_ends_time' | 'current_starts_time' | 'location'
>;

export const isOvernight = (job: JobForIsOvernight): boolean => {
  const timezone = job.location?.timezone_lookup || moment.tz.guess();
  return !moment
    .tz(job.current_starts_time, timezone)
    .isSame(moment.tz(job.current_ends_time, timezone), 'date');
};
